/* eslint-disable max-lines */
import moment from 'moment';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import CloseIcon from '@mui/icons-material/Close';
import React, { Fragment, forwardRef, useState } from 'react';
import {
  AppBar,
  Button,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Slide,
  Toolbar,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { capitalize, find, isEmpty, map, pick } from 'lodash';
import LoadingModule from 'common/components/LoadingModule';

import ModifyItemButton from './ModifyItemButton';
import ResolveAsIsButton from './ResolveAsIsButton';
import { ItemLabelValue } from '../../PatientView/components/PrescriptionsTab/PatientPrescriptionCustomLabels';
import ChangeReviewReasonDialog from './changeReasonButton';

const Transition = forwardRef((props, ref) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <Slide direction='up' ref={ref} {...props} timeout={300} />
));

const MultiRxReviewDialog = ({ open, onClose, prescriptionId, isSplitRx, orderId, fillType }) => {
  const [openChangeReasonDialog, setOpenChangeReasonDialog] = useState(false);
  const { currentPage, pages } = useSelector(({ workQueue }) => workQueue?.prescriptionReview);
  const prescriptions = pages[currentPage];
  const prescription = find(prescriptions, { pomPrescriptionId: prescriptionId });
  const { rxNumber, patient: patientInfo, prescriptionData, lineItem, status } = prescription;
  const { request, practice, originatingPharmacy } = prescriptionData;
  const instructions = request?.medication?.instructions;
  const prescriber = request?.medication?.prescriber;
  // POM-6354: the prescriber address is the practice address -- core resolves it into
  // originalPrescriber.address; without a resolved provider, the review data's practice address.
  // The address stored on the partner request (prescriber.addressLine1..) is never shown.
  const prescriberAddress =
    prescriptionData.originalPrescriber?.address ||
    (practice?.line1 ? pick(practice, ['line1', 'line2', 'city', 'state', 'zip']) : null);

  const originatingPharmacyAddress = originatingPharmacy?.pharmacyAddress;

  const prescriptionOverrideRecord =
    !isEmpty(prescriptionData?.prescriptionOverride?.drugProfile) &&
    !isEmpty(prescriptionData?.prescriptionOverride?.sig);

  const isDetailLoading = !prescription?.prescriptionDataLoaded;
  return (
    <Dialog open={open} fullScreen TransitionComponent={Transition}>
      <AppBar position='static'>
        <Toolbar variant='dense'>
          <Grid container sx={{ p: 2 }} justifyContent='space-between' alignItems='center'>
            <Grid item justifyContent='center' alignItems='center'>
              {!isSplitRx && <Typography variant='h4'>Multi Rx Prescription Review</Typography>}
              {isSplitRx && <Typography variant='h4'>Modify Prescription</Typography>}
            </Grid>

            <Grid item>
              <IconButton
                onClick={onClose}
                id={`Close Prescription View  ${prescriptionId}`}
                aria-label='Close Prescription View'
                className='CloseIconBtn'
              >
                <CloseIcon fontSize='medium' />
              </IconButton>
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
      <DialogContent>
        {isDetailLoading ? (
          <LoadingModule />
        ) : (
          <>
            <Grid container justifyContent='end' alignItems='end'>
              {prescriptionOverrideRecord && (
                <>
                  <Button
                    variant='outlined'
                    color='warning'
                    sx={{ mr: 2, width: '130px' }}
                    onClick={() => setOpenChangeReasonDialog(true)}
                  >
                    Change Reason
                  </Button>
                  <ChangeReviewReasonDialog
                    open={openChangeReasonDialog}
                    patientId={patientInfo.mpi}
                    onClose={() => setOpenChangeReasonDialog(false)}
                    lineItem={lineItem.requestLineItemId}
                    prescriptionId={prescriptionId}
                    orderId={orderId}
                    drugName={prescriptionData?.drug}
                    strength={prescriptionData?.strength}
                    form={prescriptionData?.form}
                    drugUnit={prescriptionData?.drugUnit}
                    firstName={patientInfo.firstName}
                    lastName={patientInfo.lastName}
                    dob={patientInfo.dob}
                    email={patientInfo.email}
                    gender={patientInfo.gender}
                    reviewType={status}
                  />
                </>
              )}
              {prescriptionOverrideRecord && (
                <ResolveAsIsButton
                  patientId={patientInfo.mpi}
                  lineItemId={lineItem.requestLineItemId}
                  prescriptionUUID={prescriptionId}
                />
              )}
              {fillType === 'FIRST_FILL' && (
                <ModifyItemButton
                  rxNumber={rxNumber}
                  patientId={patientInfo.mpi}
                  instructions={instructions}
                  lineItemId={lineItem.requestLineItemId}
                  prescriptionId={prescriptionId}
                  prescriptionData={prescriptionData}
                />
              )}
            </Grid>
            <Grid container>
              <Grid item xs={12} sm={6} md={6} sx={{ px: 2 }}>
                <Grid container direction='column' gap={1}>
                  <Typography variant='h6' component='h3' sx={{ fontWeight: 600, mt: 3 }}>
                    Partner
                  </Typography>

                  <ItemLabelValue
                    label='Partner Name'
                    value={prescriptionData?.partnerName || 'N/A'}
                  />

                  <Typography variant='h6' component='h3' sx={{ fontWeight: 600, mt: 3 }}>
                    Patient
                  </Typography>

                  <ItemLabelValue label='QS1Patient ID' value={patientInfo.qs1Id} />

                  <ItemLabelValue
                    label='Patient'
                    value={
                      patientInfo?.middleName
                        ? `${patientInfo?.firstName} ${patientInfo?.middleName} ${patientInfo?.lastName}`
                        : `${patientInfo?.firstName} ${patientInfo?.lastName}`
                    }
                  />
                  <ItemLabelValue label='Phone Number' value={patientInfo.mobilePhone || 'N/A'} />
                  <ItemLabelValue label='Email' value={patientInfo.email || 'N/A'} />
                  <ItemLabelValue
                    label='Address'
                    value={patientInfo?.address?.addressLine1 || 'N/A'}
                  />
                  {patientInfo?.address?.addressLine2 && (
                    <ItemLabelValue value={patientInfo?.address?.addressLine2} />
                  )}
                  <ItemLabelValue
                    value={`${patientInfo?.address?.city}, ${patientInfo?.address?.state}, ${patientInfo?.address?.zip}`}
                    labelHeight='40px'
                  />

                  <ItemLabelValue
                    label='DOB'
                    value={moment(patientInfo.dob).format('MM-DD-YYYY')}
                  />
                  <ItemLabelValue label='Sex' value={capitalize(patientInfo.gender)} />
                  <ItemLabelValue label='Patient ID' value={patientInfo.mpi} />

                  <Typography variant='h6' component='h3' sx={{ fontWeight: 600, mt: 3 }}>
                    Practice
                  </Typography>
                  <ItemLabelValue label='Practice name' value={practice?.practiceName || 'N/A'} />
                  <ItemLabelValue label='Practice ID' value={practice?.practiceId || 'N/A'} />

                  <ItemLabelValue label='Prescriber' value={prescription?.prescriber || 'N/A'} />

                  <ItemLabelValue
                    label='Electronically Signed by'
                    value={practice?.electronicallySignedBy || 'N/A'}
                  />
                  <ItemLabelValue label='Supervisor' value={practice?.supervisor || 'N/A'} />
                  <ItemLabelValue label='Phone' value={prescriber?.phone || 'N/A'} />
                  <ItemLabelValue label='Fax' value={prescriber?.fax || 'N/A'} />
                  <ItemLabelValue label='Address' value={prescriberAddress?.line1 || 'N/A'} />
                  {prescriberAddress?.line2 && <ItemLabelValue value={prescriberAddress.line2} />}
                  {prescriberAddress?.line1 && (
                    <ItemLabelValue
                      value={`${prescriberAddress.city}, ${prescriberAddress.state} ${prescriberAddress.zip}`}
                    />
                  )}
                  <ItemLabelValue label='DEA' value={practice?.prescriberDea || 'N/A'} />
                  <ItemLabelValue label='NPI' value={practice?.prescriberNpi || 'N/A'} />
                  <ItemLabelValue label='Ordering Agent' value={practice?.orderingAgent || 'N/A'} />
                </Grid>
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <Grid container direction='column' gap={1}>
                  <Typography variant='h6' component='h3' sx={{ fontWeight: 600, mt: 3 }}>
                    Drug
                  </Typography>
                  <ItemLabelValue
                    label='Prescriber Description'
                    value={prescriptionData?.drug ?? 'N/A'}
                  />

                  <ItemLabelValue label='Strength' value={prescriptionData?.strength ?? 'N/A'} />

                  <ItemLabelValue label='Drug Form' value={prescriptionData?.form ?? 'N/A'} />

                  <ItemLabelValue label='Direction' value={instructions || 'N/A'} />

                  <ItemLabelValue label='Notes' value={prescription?.rxNotes || 'N/A'} />

                  <ItemLabelValue label='Rx Number' value={prescription?.rxNumber || 'N/A'} />
                  <ItemLabelValue label='Quantity' value={prescriptionData?.quantityPrescribed} />

                  <ItemLabelValue label='Days Supply' value={prescription?.daysSupply || 'N/A'} />
                  <ItemLabelValue
                    label='Refills'
                    value={prescriptionData?.refillsRemaining || 'N/A'}
                  />

                  {prescription.refillsAuthorized && (
                    <ItemLabelValue
                      label='Refills Dispensed '
                      value={`${
                        Number(prescription?.refillsAuthorized) -
                        Number(prescription.refillsRemaining)
                      }/${prescription.refillsAuthorized}`}
                    />
                  )}

                  <ItemLabelValue label='DAW' value={prescription?.daw || 'N/A'} />
                  <ItemLabelValue
                    label='Date Written'
                    value={
                      prescriptionData?.writtenDate
                        ? moment(prescriptionData.writtenDate).format('MM-DD-YYYY')
                        : 'N/A'
                    }
                  />
                  <ItemLabelValue
                    label='Expiration Date'
                    value={
                      prescription?.expirationDate
                        ? moment(prescription.expirationDate).format('MM-DD-YYYY')
                        : 'N/A'
                    }
                  />
                  <ItemLabelValue
                    label='LastFill Date'
                    value={
                      prescription?.lastFillDate
                        ? moment(prescription.lastFillDate).format('MM-DD-YYYY')
                        : 'N/A'
                    }
                  />

                  {originatingPharmacy?.name && (
                    <>
                      <Typography variant='h6' component='h3' sx={{ fontWeight: 600, mt: 3 }}>
                        Originating Pharmacy
                      </Typography>
                      <ItemLabelValue label='Pharmacy' value={originatingPharmacy?.name} />
                      <ItemLabelValue label='Pharmacy NPI' value={originatingPharmacy?.npi} />
                      <ItemLabelValue
                        label='Pharmacist'
                        value={originatingPharmacy?.pharmacistName}
                      />
                      <ItemLabelValue
                        label='Address'
                        value={originatingPharmacyAddress?.addressLine1}
                      />
                      <ItemLabelValue value={originatingPharmacyAddress?.addressLine2} />
                      <ItemLabelValue
                        value={`${originatingPharmacyAddress?.addressCity}, ${originatingPharmacyAddress?.addressState} , ${originatingPharmacyAddress?.addressZip}`}
                      />
                    </>
                  )}

                  {map(prescription.transferPharmacies, (pharmacy) => (
                    <Fragment key={pharmacy?.npi}>
                      <Typography variant='h6' component='h3' sx={{ fontWeight: 600, mt: 3 }}>
                        Transfer Pharmacy
                      </Typography>
                      <ItemLabelValue label='Pharmacy' value={pharmacy?.name} />
                      <ItemLabelValue label='Pharmacy NPI' value={pharmacy?.npi} />
                      <ItemLabelValue label='Pharmacist' value={pharmacy?.pharmacistName} />
                      <ItemLabelValue
                        label='Transfer Date'
                        value={
                          pharmacy?.transferDate
                            ? format(new Date(pharmacy?.transferDate), 'MM/dd/yyyy hh:mmaaa')
                            : undefined
                        }
                      />
                      <ItemLabelValue
                        label='Address'
                        value={pharmacy?.pharmacyAddress?.addressLine1}
                      />
                      <ItemLabelValue value={pharmacy?.pharmacyAddress?.addressLine2} />
                      <ItemLabelValue
                        value={`${pharmacy?.pharmacyAddress?.addressCity}, ${pharmacy?.pharmacyAddress?.addressState} , ${pharmacy?.pharmacyAddress?.addressZip}`}
                      />
                    </Fragment>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MultiRxReviewDialog;

MultiRxReviewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  prescriptionId: PropTypes.string.isRequired,
  isSplitRx: PropTypes.bool,
  orderId: PropTypes.string,
  fillType: PropTypes.string,
};

MultiRxReviewDialog.defaultProps = {
  orderId: PropTypes.string,
  fillType: '',
};

MultiRxReviewDialog.defaultProps = { isSplitRx: false };
